import React, { useState, useEffect, useCallback, useRef } from 'react';
import i18next from 'i18next';
import clsx from 'clsx';
import dayjs from 'dayjs';

import { useParams, useHistory } from 'react-router-dom';

import axiosInstance from '../../../config/axiosConfig';

import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import DeepKnob from './DeepKnob';
import _ from 'lodash';

import renderRecruitementQuestions from './renderRecruitementQuestions';
import CandidateAttemptRadarChart from './CandidateAttemptRadarChart';
import CandidateAttemptPDF from './CandidateAttemptPDF';

import useSelectedCompanyStore from '../../../store/useSelectedCompanyStore';
import TestGaussianChart from '../TestGaussianChart';

const t = (i18nKey, params) => i18next.t(`userDashboard.candidate.${i18nKey}`, params);

function useCountAnimation(startValue, endValue, duration = 500) {
    const [count, setCount] = useState(startValue);

    useEffect(() => {
        let startTime;
        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const percent = Math.min(progress / duration, 1);
            setCount(Math.floor(startValue + (endValue - startValue) * percent));
            if (percent < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [startValue, endValue, duration]);

    return count;
}

const AnimatedNumber = ({ startValue, endValue, duration = 500 }) => {
    const animatedValue = useCountAnimation(startValue, endValue, duration);
    return <>{animatedValue}</>;
};

const CandidateAttemptDetails = () => {
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);

    const [subScoresLoading, setSubScoresLoading] = useState(true);
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [candidate, setCandidate] = useState(null);
    const [test, setTest] = useState(null);
    const [nextTest, setNextTest] = useState(null);
    const [nextTestLoadingLoading, setNextTestLoading] = useState(false);
    const [tab, setTab] = useState('report');
    const [ref, setRef] = useState(null);
    const [endUserScore, setEndUserScore] = useState(null);
    const [createdAt, setCreatedAt] = useState(null);

    const [subScores, setSubScores] = useState([]);
    const [subScoresScores, setSubScoresScores] = useState([]);
    const [topFourScores, setTopFourScores] = useState(null);

    const [scoreTitleText, setScoreTitleText] = useState('');
    const [scoreTexts, setScoreTexts] = useState({});
    const [oneLiners, setOneLiners] = useState({});
    const [campaign, setCampaign] = useState(null);
    const [potential, setPotential] = useState([]);
    const [gaussianChart, setGaussianChart] = useState(null);

    const [pdfUrl, setPdfUrl] = useState(null);

    const { campaign_id, candidate_id, attempt_ref } = useParams();

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const toast = useRef(null);

    const history = useHistory();

    const fetchAttemptDetails = useCallback(async () => {
        try {
            const response = await axiosInstance.get(`/dashboard/candidates/${selectedCompany?.company_id}/${candidate_id}/${attempt_ref}/getDetails`);
            setCandidate(response.data.candidate);
            setTest(response.data.test);
            setCampaign(response.data.campaign);
            setRef(response.data.ref);
            setNextTest(response.data.next_test);
            setCreatedAt(response?.data?.created_at);
            setEndUserScore(response.data.enduser_score);
            setGaussianChart(response.data?.gaussian_chart);

            await fetchUnlockedAttemptDetails(selectedCompany?.company_id, candidate_id, attempt_ref, response.data.test.ref, response.data.candidate);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Détails de la tentative non trouvés', life: 3000 });
                window.location.href = '/dashboard/campaigns/';
            } else {
                console.error(error);
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la récupération des détails de la tentative.', life: 3000 });
            }
        } finally {
            setLoading(false);
            setNextTestLoading(false);
        }
    }, [candidate_id, attempt_ref, selectedCompany?.company_id]);

    const fetchUnlockedAttemptDetails = async (company_id, candidate_id, attempt_ref, test_ref, candidate) => {
        try {
            let response = await axiosInstance.post(`/dashboard/candidates/${company_id}/${candidate_id}/unlockedAttemptDetails`, {
                attempt_ref: attempt_ref,
                test_ref: test_ref
            });

            const username = _.capitalize(candidate?.username) || 'Inconnu';
            const firstName = _.capitalize(candidate?.first_name) || 'Inconnu';
            const lastName = _.capitalize(candidate?.last_name) || 'Inconnu';
            const fullName = _.capitalize(candidate?.first_name) && candidate?.last_name ? `${firstName} ${lastName}` : 'Inconnu';

            const replacements = {
                '{{fullName}}': fullName,
                '{{username}}': username,
                '{{lastName}}': lastName,
                '{{firstName}}': firstName
            };

            const replacePlaceholders = (text) => {
                return text.replace(/{{fullName}}|{{username}}|{{lastName}}|{{firstName}}/g, (matched) => {
                    const value = replacements[matched];
                    return `<span class="has-text-dgrad-blue">${value}</span>`;
                });
            };

            for (const key in response.data.score_texts) {
                if (response.data.score_texts.hasOwnProperty(key)) {
                    response.data.score_texts[key] = replacePlaceholders(response.data.score_texts[key]);
                }
            }

            response.data.score_titles_texts = replacePlaceholders(response.data.score_titles_texts);

            setOneLiners(response.data.one_liners);
            setScoreTexts(response.data.score_texts);
            setScoreTitleText(response.data.score_titles_texts);

            setSubScores(response.data.sub_scores);
            setSubScoresScores(response.data.sub_scores_scores);
            setTopFourScores(response.data.top_four_scores);

            if (response.data.potential) {
                const potentialData = Object.keys(response.data.potential).map((key) => ({
                    title: key.charAt(0).toUpperCase() + key.slice(1),
                    value: response.data.potential[key],
                }));
                setPotential(potentialData);
            }
        } catch (error) {
            console.error('Error fetching unlocked attempt details:', error);
        } finally {
            setSubScoresLoading(false);
        }
    };

    const handleArrowClick = () => {
        if (pdfLoading) return;
        if (pdfUrl) setPdfUrl(null);
        setNextTestLoading(true);

        history.push(`/dashboard/campaigns/${campaign.campaign_id}/candidates/${candidate.candidate_id}/${nextTest.attempt_ref}`);
    };

    useEffect(() => {
        fetchAttemptDetails();
    }, [fetchAttemptDetails]);

    const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1);
    };

    const [expandedIndexes, setExpandedIndexes] = useState([]);

    const toggleExpand = (index) => {
        setExpandedIndexes((prevIndexes) =>
            prevIndexes.includes(index)
                ? prevIndexes.filter((i) => i !== index)
                : [...prevIndexes, index]
        );
    };

    const [activeChart, setActiveChart] = useState('radar');

    const fromValue = endUserScore > 10 ? endUserScore - 10 : 0;
    const animatedScore = useCountAnimation(fromValue, endUserScore, 500);

    if (loading) {
        return (
            <div className="loading-page">
                <div className="loading-container">
                    <div className="loading-spinner">
                        <i className="fa-solid fa-circle-notch fa-spin fa-3x"></i>
                    </div>
                </div>
            </div>
        );
    }

    if (!candidate || !test || !campaign) {
        return null;
    }

    return (
        <>
            <Toast ref={toast} position="bottom-right" />

            <section className="home">
                <div className="container">
                    <div className="columns is-vcentered is-multiline">
                        <div className="column">
                            <h1 className="title is-4 mb-0">
                                {`${candidate.first_name || ''} ${candidate.last_name || ''} ${candidate.username ? `- ${candidate.username}` : ''}`} <span className="subtitle is-6 has-text-grey-dark">({candidate?.email || 'Aucun e-mail enregistré'})</span>
                            </h1>
                        </div>
                    </div>

                    <div className="columns">
                        <div className="column">

                            <div className='mb-5'>
                                <span className="subtitle is-5 has-text-grey has-text-weight-bold mb-0">{campaign?.campaign_name} -  <span className="is-size-6 has-text-grey mt-0 mb-5">{selectedCompany?.name}</span></span>
                            </div>

                            <div className="column columns pl-0">

                                <div className="column">

                                    <p className="mb-0">
                                        <span className="icon has-text-success mr-1">
                                            <i className="fa-solid fa-circle-check" style={{ color: '#00a8d7' }}></i>
                                        </span>
                                        {t('test_realized')} <strong>{dayjs(createdAt).format('DD/MM/YY - HH:mm')}</strong>
                                    </p>

                                </div>

                                <div className="column has-text-right">
                                    {test?.category !== 'health' && <a className='button is-outlined radius-8 mr-1'
                                        onClick={() => { history.push(`/dashboard/campaigns/${campaign_id}/candidates/compare`); }}
                                    ><span className="icon">
                                            <i className="fas fa-balance-scale"></i>
                                        </span>
                                        <span>Comparer ces résultats</span>
                                    </a>}

                                    <CandidateAttemptPDF
                                        data={{ company_id: selectedCompany?.company_id, candidate_id, attempt_ref }}
                                        pdfLoading={pdfLoading}
                                        setPdfLoading={setPdfLoading}
                                        pdfUrl={pdfUrl}
                                        setPdfUrl={setPdfUrl}
                                        toast={toast}
                                    />
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="tabs is-boxed is-centered">
                        <ul className='m-0'>
                            <li className={clsx({ 'is-active': tab === 'report' })} onClick={() => setTab('report')}>
                                <a>
                                    <span>Rapport</span>
                                </a>
                            </li>
                            <li className={clsx({ 'is-active': tab === 'questions' })} onClick={() => setTab('questions')}>
                                <a>
                                    {test?.category === 'health' ?
                                        (<span>{t('recruitement_question')}</span>) :
                                        (<span>{t('health_question')}</span>)
                                    }
                                </a>
                            </li>
                        </ul>
                    </div>

                    {tab === 'report' && !subScoresLoading &&
                        (<>
                            <Sidebar className='primary-text is-width-10' visible={infoModalOpen} position="right" onHide={() => setInfoModalOpen(false)}>
                                <>
                                    <h1 className="title deepsy-color has-text-centered">{i18next.t("tests.resultsPage.infoModal.title")}</h1>
                                    <p className='p-6 text-is-hyphens has-text-weight-light' style={{ lineHeight: '138%' }} dangerouslySetInnerHTML={{ __html: i18next.t("tests.resultsPage.infoModal.subtitle") }} />

                                    <div className='has-text-centered'>
                                        <img src="/images/test/testResults_explications.png" alt="Mental map like" />
                                    </div>

                                    <p className='p-6 text-is-hyphens has-text-weight-light' style={{ lineHeight: '138%' }} dangerouslySetInnerHTML={{ __html: i18next.t("tests.resultsPage.infoModal.content") }} />
                                    <div className="buttons is-justify-content-center pb-6">
                                        <a target='_blank' href="/faq" className="button is-link is-outlined">{i18next.t('tests.resultsPage.infoModal.access_faq')}</a>
                                        <a target='_blank' className="button" onClick={() => setInfoModalOpen(false)}>{i18next.t('tests.resultsPage.infoModal.backToResults')}</a>
                                    </div>
                                </>
                            </Sidebar>

                            <div className="container is-widescreen pb-6">
                                <div className="columns mb-6">
                                    <div className="column is-7">
                                        <h1 className="title is-2 has-text-centered color-transition mb-5"
                                            style={{ color: test?.test_color ? test?.test_color : '#5684c1' }}>
                                            {test?.sub_title || ''}
                                        </h1>

                                        <div className="score-container is-justify-content-center is-flex is-align-items-center color-transition">
                                            <h2 className="subtitle is-1 mt-3 mb-1 is-flex is-align-items-center color-transition"
                                                style={{ color: test?.test_color ? test?.test_color : '#67bde9' }}>
                                                {animatedScore}%
                                                <i className="pi pi-info-circle is-clickable is-size-5 ml-1" onClick={() => setInfoModalOpen(true)}></i>
                                            </h2>
                                        </div>

                                        <div className="progress-container radius-15 mb-6" style={{ maxWidth: 450, margin: '0 auto' }}>
                                            <div className="progress-bar color-transition"
                                                style={{ width: `${endUserScore}%`, backgroundColor: test?.test_color || '#67bde9' }}>
                                            </div>
                                        </div>

                                        <p className="is-size-5 text-is-hyphens has-text-weight-light"
                                            style={{ lineHeight: '138%' }}
                                            dangerouslySetInnerHTML={{ __html: scoreTitleText }}>
                                        </p>
                                    </div>

                                    <div className="column is-5">
                                        <div className="has-text-right mb-4">
                                            {nextTest && nextTest?.attempt_ref &&
                                                <i
                                                    key={nextTest.attempt_ref}
                                                    onClick={handleArrowClick}
                                                    className={clsx('color-transition', {
                                                        'opacity-transition is-not-allowed': pdfLoading,
                                                        'is-clickable': !pdfLoading,
                                                        'fas fa-arrow-right': !nextTestLoadingLoading,
                                                        'fa-solid fa-circle-notch fa-spin': nextTestLoadingLoading
                                                    })}
                                                    style={{
                                                        fontSize: '3rem',
                                                        background: `linear-gradient(to right, ${test?.test_color ? test?.test_color : '#5684c1'}, ${nextTest?.test_color ? nextTest?.test_color : '#5684c1'})`,
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent',
                                                        opacity: pdfLoading ? '60%' : ''
                                                    }}
                                                ></i>
                                            }
                                        </div>

                                        <div className="columns is-multiline">
                                            {test?.ref !== "idees-noires" && (
                                                <>
                                                    <div className="column is-12">
                                                        <div className="tabs is-centered is-small mb-2">
                                                            <ul className='m-0'>
                                                                <li className={clsx({ 'is-active': activeChart === 'radar' })}>
                                                                    <a onClick={() => setActiveChart('radar')}>
                                                                        <span className="icon"><i className="fa-brands fa-connectdevelop"></i></span>
                                                                        <span>Radar</span>
                                                                    </a>
                                                                </li>
                                                                <li className={clsx({ 'is-active': activeChart === 'gaussian' })}>
                                                                    <a onClick={() => setActiveChart('gaussian')}>
                                                                        <span className="icon"><i className="fa-solid fa-chart-simple"></i></span>
                                                                        <span>Distribution</span>
                                                                    </a>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                        {activeChart === 'radar' ? (
                                                            <CandidateAttemptRadarChart
                                                                data={{
                                                                    title: test?.title,
                                                                    color: test?.test_color,
                                                                    top_four_scores: topFourScores,
                                                                    endUserScore
                                                                }}
                                                            />
                                                        ) : (
                                                            gaussianChart && (
                                                                <TestGaussianChart
                                                                    gaussian_chart={gaussianChart}
                                                                    test_color={test?.test_color}
                                                                    candidate_name={candidate?.first_name || 'Inconnu'}
                                                                    candidate_score={endUserScore}
                                                                />
                                                            )
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <hr className="is-invisible" />

                            <div className="container is-widescreen mt-5">
                                {subScores.map((score, index) => (
                                    <div key={index} className="score-section">
                                        <div className="columns is-vcentered is-clickable mb-0" style={{ maxHeight: 40 }} onClick={() => toggleExpand(index)}>
                                            <div className="column is-7 pl-0">
                                                <p className='is-size-3 has-text-dgrad-blue has-text-weight-semibold pl-2'>
                                                    <span className={clsx("arrow", { expanded: expandedIndexes.includes(index) })}><i className="fa-solid fa-caret-right"></i></span> {capitalizeFirstLetter(score)}
                                                </p>
                                            </div>

                                            <div className="column">
                                                <div className="progress-container radius-15" style={{ height: 20 }}>
                                                    <div
                                                        className="progress-bar color-transition"
                                                        style={{
                                                            width: `${subScoresScores[index]}%`,
                                                            backgroundColor: test?.test_color || '#5684c1',
                                                        }}>
                                                    </div>
                                                </div>

                                            </div>

                                            <div className="column is-1 pr-0">
                                                <p
                                                    className='text-is-hyphens color-transition'
                                                    style={{ fontSize: 25, color: test?.test_color || '#4786cb', fontWeight: 600 }}
                                                >
                                                    <AnimatedNumber
                                                        startValue={subScoresScores[index] > 10 ? subScoresScores[index] - 10 : 0}
                                                        endValue={subScoresScores[index]}
                                                        duration={500}
                                                    />%
                                                </p>
                                            </div>
                                        </div>

                                        <div className={clsx("score-text", { hidden: !expandedIndexes.includes(index) })}>
                                            <p className="text-is-hyphens has-text-weight-light is-size-5 mt-5" style={{ lineHeight: '138%' }} dangerouslySetInnerHTML={{ __html: scoreTexts[index] }} />

                                            {oneLiners && (<>
                                                {oneLiners[score] && <h3 className='one_liner_title mt-2'>Soft-skills associés</h3>}

                                                <div className='one_liners_wrapper pb-5'>

                                                    {oneLiners[score]?.map((line, index) => (
                                                        (JSON.parse(line.content)[window.locale] || JSON.parse(line.content).fr).map((content, subIndex) => (
                                                            <div key={`${index}-${subIndex}`} className='one_liner_item'>
                                                                <span className='one_liner_text'>{content}</span>
                                                            </div>
                                                        ))
                                                    ))}

                                                </div>

                                            </>)}

                                        </div>

                                        <hr className="is-invisible" />

                                    </div>
                                ))}

                                <hr className="is-invisible" />

                            </div>

                            <hr className="is-invisible" />

                            {potential.length > 0 && (
                                <div className="container is-widescreen">
                                    {test?.category === 'health' ?
                                        <span className='title is-3 mb-6 has-text-dgrad-deepsy'>Analyse des facteurs psycho-sociaux</span>
                                        :
                                        <span className='title is-3 mb-6 has-text-dgrad-blue'>Mesure du potentiel de {_.capitalize(candidate?.first_name)}</span>
                                    }

                                    <div className="columns is-multiline is-justify-content-space-evenly has-text-centered mt-6">
                                        {potential.map((knob, index) => (
                                            <div className="column is-one-third" key={index}>
                                                {/* <span className='subtitle is-4 mt-2 has-text-dgrad-blue'>{knob.title}</span> */}
                                                <span className='subtitle is-4 mt-2' style={{ color: test?.test_color }}>{knob.title}</span>

                                                <div className='mt-5'>
                                                    <DeepKnob
                                                        value={knob.value}
                                                        size={150}
                                                        valueColor={test?.test_color}
                                                        textColor={test?.test_color}
                                                        strokeWidth={25}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </>)
                    }

                    {tab === 'questions' && renderRecruitementQuestions({ test })}

                </div>
            </section>
        </>
    );
};

export default CandidateAttemptDetails;